import classNames from "classnames"
import React from "react"

import Loader from "./Loader"
import TextButton from "../components/elements/V2/Buttons/TextButton"
import GatsbyStoryblokImage from "../components/elements/V2/GatsbyStoryblokImage"
import CloseIcon from "../components/elements/V2/Icons/CloseIcon"
import { Color } from "../constants/V2/color"

import Typography, { TypographySize } from "@components/elements/V2/Typography"
import {
  getBackgroundColorClass,
  getAccentColor,
  getTextColorClass,
} from "@utils/V2/color"
import { ToastVariant } from "notifications/types"

export interface ToastProps {
  icon?: string | React.ReactNode
  iconAlt?: string
  text: string
  typographySize?: TypographySize
  variant?: ToastVariant
  linkUrl?: string | YocoCom.YocoLink
  linkColor?: Color
  linkText?: string
  trackingOptions?: TrackingOptions
  className?: string
  linkOnClick?: VoidFunction
  onClick?: VoidFunction
  isLinkLoading?: boolean
  title?: string
}

interface TrackingOptions {
  name: string
  key: string
  value: string
}

const getBackgroundColor = (variant: ToastVariant): Color => {
  switch (variant) {
    case "success":
      return Color.Green
    case "alert":
      return Color.Pink
    case "warning":
      return Color.Yellow
    case "info-dark":
      return Color.Blue
    default:
      return Color.LightBlue
  }
}

const Toast = ({
  icon,
  iconAlt = "",
  variant = "info",
  text,
  typographySize = "body-md",
  linkUrl,
  linkColor = Color.Blue,
  linkText,
  trackingOptions,
  className,
  linkOnClick,
  onClick,
  isLinkLoading,
  title,
}: ToastProps) => {
  const backgroundColor = getBackgroundColor(variant)

  const textColor = getAccentColor(backgroundColor)

  return (
    <div
      className={classNames(
        "flex min-h-56 items-start space-x-[8px] rounded-[10px] px-16 py-16 sm:flex-row sm:items-center lg:space-x-[10px] lg:px-20",
        getBackgroundColorClass(backgroundColor),
        className
      )}
    >
      <div className="flex items-start gap-[6px]">
        {icon &&
          (typeof icon === "string" ? (
            <GatsbyStoryblokImage
              image={icon}
              alt={iconAlt || "Toast Icon"}
              className="h-20 w-20 md:h-24 md:w-24"
            />
          ) : (
            <div className="h-20 min-w-[14px] md:h-24 md:min-w-16">{icon}</div>
          ))}

        <div className="inline-block md:pt-[2px]">
          {title && (
            <Typography
              weight="medium"
              font="grotesk"
              size={typographySize}
              className="mr-4 inline"
              color={textColor}
              text={title}
            />
          )}
          <Typography
            weight="book"
            font="grotesk"
            size={typographySize}
            className="mr-4 inline"
            color={textColor}
            text={text}
          />
          {linkText &&
            (isLinkLoading ? (
              <Loader
                color="currentColor"
                className={classNames(
                  getTextColorClass(linkColor || textColor),
                  "inline"
                )}
              />
            ) : (
              <TextButton
                style="text"
                weight="medium"
                uppercase={false}
                color={linkColor || textColor}
                className="inline !gap-0 !py-[3px]"
                textSize={typographySize}
                text={linkText}
                trackingEvent={
                  trackingOptions ? trackingOptions.name : undefined
                }
                trackingEventKey={
                  trackingOptions ? trackingOptions.key : undefined
                }
                trackingEventValue={
                  trackingOptions ? trackingOptions.value : undefined
                }
                onClick={() => linkOnClick?.()}
                linkUrl={linkUrl}
              />
            ))}
        </div>
      </div>

      {onClick && (
        <div className="h-24 w-24 cursor-pointer">
          <CloseIcon
            width="24px"
            height="24px"
            hideBorder
            strokeWidth="2"
            onClick={onClick}
            strokeColor={textColor}
          />
        </div>
      )}
    </div>
  )
}

export default Toast
